import { FC, useEffect, useState } from 'react';
import { Modal, Checkbox, Button, message } from '@tencent/tea-component';
import { useTranslation } from 'react-i18next';

import { ModalShowHandle } from '@tencent/tea-component/lib/modal/ModalShow';
import { DialogProps, IBtnConfig, OpenDialog } from './Dialog.type';

import './Dialog.scss';

function isPromise(p: any) {
  return p !== null && (typeof p === 'object' || typeof p === 'function') && typeof p.then === 'function';
}

let DIALOG: ModalShowHandle;

const DialogContent: FC<DialogProps> = (props) => {
  const { t } = useTranslation();

  const [checked, setChecked] = useState((props?.checkboxConfig ?? []).filter((el) => el.checked).map((el) => el.name));
  const [loading, setLoading] = useState(false);
  const { okText, onOk, cancelText, onCancel } = props;

  const close = () => {
    if (DIALOG) {
      if (props.dialogId) {
        DialogMap[props.dialogId] = false;
      }

      DIALOG.destroy();
    }
  };

  const onBtnClick = (callback: IBtnConfig['callback']) => {
    if (typeof callback !== 'function') {
      close();
      return;
    }
    const res = callback(checked as string[]); // 把checkbox的状态带出去，默认false
    // callback 可以是Promise
    if (isPromise(res)) {
      setLoading(true);
      (res as Promise<void>)
        .then(() => {
          close(); // 已经destroy 不需要setLoading
        })
        .catch((err) => {
          setLoading(false);
          message.error({
            content: err,
          });
        });
    } else {
      close();
    }
  };

  const okActionElement = (
    <Button
      className="dialog-btn"
      type="primary"
      loading={loading}
      disabled={loading}
      onClick={() => {
        onBtnClick(onOk);
      }}
    >
      {okText || t('我知道了')}
    </Button>
  );
  const cancelActionElement = (
    <Button
      className="dialog-btn"
      type="weak"
      loading={loading}
      disabled={loading}
      onClick={() => {
        onBtnClick(onCancel);
      }}
    >
      {cancelText || t('取消')}
    </Button>
  );
  const renderDialogAction = () =>
    (okText || onOk || cancelText || onCancel) && (
      <div className="btn-list">
        {(cancelText || onCancel) && cancelActionElement}
        {(okText || onOk) && okActionElement}
      </div>
    );

  const isSingleTitle = props.title && !props.content && !((props.checkboxConfig?.length ?? 0) > 0);
  return (
    <div className={`dialog-content ${isSingleTitle ? 'dialog-signle-title' : ''}`}>
      <div className="dialog-body">
        {props.title && <div className="dialog-title">{props.title}</div>}
        {props.content && <div className="dialog-desc">{props.content}</div>}
        {props.checkboxConfig?.length ? (
          <div className="dialog-tips">
            <Checkbox.Group value={checked as string[]} onChange={(value) => setChecked(value)} layout="column">
              {props.checkboxConfig.map((el) => (
                <Checkbox value={el.checked} key={el.name} name={el.name}>
                  {el.checkboxText}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        ) : null}
      </div>

      {(props.btnConfigs?.length ?? 0) > 0 ? (
        <div className="btn-list">
          {(props.btnConfigs as IBtnConfig[]).map((btn: IBtnConfig, index) =>
            btn ? (
              <Button
                className="dialog-btn"
                type={btn.type}
                loading={loading}
                disabled={loading}
                onClick={() => {
                  onBtnClick(btn.callback);
                }}
                key={index}
              >
                {btn.text}
              </Button>
            ) : null,
          )}
        </div>
      ) : (
        renderDialogAction()
      )}
    </div>
  );
};

DialogContent.defaultProps = {
  checkboxConfig: [],
};

const onceDialogMap: Record<string, boolean> = {};

// dialog只允许出现一次
const DialogMap: Record<string, boolean> = {};

export const openDialog: OpenDialog = (props) => {
  if (props.showOnce) {
    if (onceDialogMap[props?.onceTag ?? '']) {
      // 已经出现过了, 直接return
      return;
    }
    onceDialogMap[props?.onceTag ?? ''] = true;
  }

  if (props.dialogId) {
    // 同时只能出现一个 已经出现过了, 直接return
    if (DialogMap[props.dialogId]) {
      return;
    }
    DialogMap[props?.dialogId ?? ''] = true;
  }

  DIALOG = Modal.show({
    className: 'base-dialog',
    maskClosable: props.maskClosable,
    disableCloseIcon: true,
    disableEscape: props.disableEscape,
    children: (
      <DialogContent
        dialogId={props.dialogId}
        title={props.title}
        content={props.content}
        checkboxConfig={props.checkboxConfig}
        btnConfigs={props.btnConfigs}
        okText={props.okText}
        onOk={props.onOk}
        cancelText={props.cancelText}
        onCancel={props.onCancel}
      />
    ),
  });

  return DIALOG;
};

// 直接使用 openDialog，在组件卸载的时候需要主动执行 openDialog().destory(),  封装一个自定义hook
export const useOpenDialog = () => {
  useEffect(
    () => () => {
      if (DIALOG) {
        DIALOG.destroy();
      }
    },
    [],
  );
  return openDialog;
};
